// Image attachment helpers: resample if needed, hash, presign, PUT.
//
// The browser does the heavy lifting so we keep the API tier dumb. We resample
// only when the source is too big — small chart screenshots upload as-is so
// fine text stays crisp.

export const MAX_DIM = 2048;
/// Skip resampling when both dimensions and file size are under these caps.
export const MAX_BYTES_NO_RESAMPLE = 4 * 1024 * 1024; // 4 MB
/// Hard reject anything larger than this before decoding (defends against
/// OOM-on-decode of pathological inputs).
export const MAX_ORIGINAL_BYTES = 25 * 1024 * 1024; // 25 MB

export type ImageFormat = 'png' | 'jpeg' | 'gif' | 'webp';

export interface AssetRef {
  s3_key: string;
  format: ImageFormat;
  sha256: string;
  original_filename?: string | null;
  byte_size?: number | null;
}

const ALLOWED: Record<string, ImageFormat> = {
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/jpg': 'jpeg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

function contentTypeToFormat(ct: string): ImageFormat | null {
  return ALLOWED[ct.toLowerCase()] ?? null;
}

/// Resample to PNG if dimensions or file size exceed thresholds; otherwise
/// return the original blob untouched.
///
/// PNG (not JPEG) on resample: we expect chart screenshots where text edges
/// matter more than file size. JPEG's chroma subsampling eats small text.
async function resampleIfNeeded(
  file: File,
): Promise<{ blob: Blob; format: ImageFormat }> {
  const sourceFormat = contentTypeToFormat(file.type);
  if (!sourceFormat) {
    throw new Error(`unsupported image type: ${file.type}`);
  }

  const fitsAsIs = file.size <= MAX_BYTES_NO_RESAMPLE;
  if (fitsAsIs) {
    // Quick dimension check still required — a 200KB 10000x10000 PNG exists.
    const bmp = await createImageBitmap(file);
    try {
      if (bmp.width <= MAX_DIM && bmp.height <= MAX_DIM) {
        return { blob: file, format: sourceFormat };
      }
    } finally {
      bmp.close();
    }
  }

  const bmp = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_DIM / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('OffscreenCanvas 2d context unavailable');
    ctx.drawImage(bmp, 0, 0, w, h);
    const blob = await canvas.convertToBlob({ type: 'image/png' });
    return { blob, format: 'png' };
  } finally {
    bmp.close();
  }
}

async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

interface PresignResponse {
  already_uploaded: boolean;
  asset: AssetRef;
  upload?: {
    url: string;
    method: string;
    required_headers: Record<string, string>;
    expires_in_secs: number;
  };
}

// ---------------------------------------------------------------------------
// Structured errors
// ---------------------------------------------------------------------------

/// Discriminated union covering every failure mode of the upload pipeline.
/// The UI matches on `kind` rather than parsing strings — backend message
/// changes never break the user-facing copy.
export type UploadErrorKind =
  | { kind: 'too_large'; sizeBytes: number; maxBytes: number }
  | { kind: 'unsupported_type'; contentType: string }
  | { kind: 'invalid_input'; backendMessage: string }
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden' }
  /// Backend route is missing — most often the API hasn't been deployed yet.
  | { kind: 'service_unavailable'; status: number }
  | { kind: 'rate_limited'; retryAfterSecs?: number }
  | { kind: 'server_error'; status: number }
  /// S3 itself rejected the upload (e.g. checksum mismatch). User-driven
  /// abuse or a backend bug — almost never an actionable user error.
  | { kind: 'storage_rejected'; status: number }
  | { kind: 'network'; cause: string };

export class AssetUploadError extends Error {
  readonly detail: UploadErrorKind;
  /// Pre-rendered, user-facing copy. Use this directly in the UI.
  readonly userMessage: string;

  constructor(detail: UploadErrorKind, userMessage: string) {
    super(userMessage);
    this.name = 'AssetUploadError';
    this.detail = detail;
    this.userMessage = userMessage;
  }
}

function mbStr(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/// Build an `AssetUploadError` from a non-OK presign response. Status code
/// is the only thing we read — we do not inspect the body text.
function presignErrorFromStatus(
  status: number,
  body: { message?: unknown; retry_after_secs?: unknown } | null,
): AssetUploadError {
  switch (status) {
    case 400: {
      const backendMessage =
        typeof body?.message === 'string' ? body.message : 'invalid request';
      return new AssetUploadError(
        { kind: 'invalid_input', backendMessage },
        // Backend's structured 400 messages are short and user-safe (size/
        // type/hash validation strings), so it's worth surfacing them.
        `Couldn't upload image: ${backendMessage}.`,
      );
    }
    case 401:
      return new AssetUploadError(
        { kind: 'unauthenticated' },
        'Your session expired. Sign in again to upload images.',
      );
    case 403:
      return new AssetUploadError(
        { kind: 'forbidden' },
        "You don't have permission to upload images to this workspace.",
      );
    case 404:
      return new AssetUploadError(
        { kind: 'service_unavailable', status },
        'Image upload is not available right now. The service may still be deploying — try again in a few minutes.',
      );
    case 429: {
      const retryAfterSecs =
        typeof body?.retry_after_secs === 'number' ? body.retry_after_secs : undefined;
      return new AssetUploadError(
        { kind: 'rate_limited', retryAfterSecs },
        retryAfterSecs
          ? `Too many uploads — try again in ${retryAfterSecs}s.`
          : 'Too many uploads — please wait a moment and try again.',
      );
    }
    default:
      if (status >= 500 && status < 600) {
        return new AssetUploadError(
          { kind: 'server_error', status },
          'The upload service hit an error. Please try again.',
        );
      }
      // 502/503/504 fall into the 5xx branch above; anything else (e.g. 408)
      // we treat as a server problem rather than user-actionable.
      return new AssetUploadError(
        { kind: 'server_error', status },
        `Image upload failed (${status}). Please try again.`,
      );
  }
}

/// End-to-end: validate → resample → hash → presign → PUT → return AssetRef.
///
/// The returned `AssetRef` is what the caller attaches to a chat message. The
/// `previewUrl` is a `blob:` URL the caller can render immediately without
/// waiting on a presigned-download round-trip; it's valid until you call
/// `URL.revokeObjectURL` on it.
///
/// Throws [`AssetUploadError`] for every expected failure — the UI should
/// render `error.userMessage` rather than parsing `error.message`.
export async function uploadAsset(opts: {
  workspaceId: string;
  file: File;
  apiUrl: string;
  token: string;
}): Promise<{ asset: AssetRef; previewUrl: string }> {
  const { workspaceId, file, apiUrl, token } = opts;

  if (file.size > MAX_ORIGINAL_BYTES) {
    throw new AssetUploadError(
      { kind: 'too_large', sizeBytes: file.size, maxBytes: MAX_ORIGINAL_BYTES },
      `Image is too large (${mbStr(file.size)}; max ${mbStr(MAX_ORIGINAL_BYTES)}).`,
    );
  }
  if (!contentTypeToFormat(file.type)) {
    throw new AssetUploadError(
      { kind: 'unsupported_type', contentType: file.type },
      'Unsupported image type. Use PNG, JPEG, GIF, or WebP.',
    );
  }

  const { blob, format } = await resampleIfNeeded(file);
  const sha256 = await sha256Hex(blob);

  // The sha256 binds the URL to the bytes; the backend computes the s3_key
  // from this hash + workspace prefix.
  let presignResp: Response;
  try {
    presignResp = await fetch(
      `${apiUrl}/api/workspaces/${workspaceId}/assets/presign`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sha256,
          content_type: blob.type || `image/${format}`,
          content_length: blob.size,
          original_filename: file.name || null,
        }),
      },
    );
  } catch (e: any) {
    throw new AssetUploadError(
      { kind: 'network', cause: e?.message ?? 'fetch failed' },
      "Couldn't reach the upload service. Check your connection and try again.",
    );
  }
  if (!presignResp.ok) {
    const body = (await presignResp.json().catch(() => null)) as
      | { message?: unknown; retry_after_secs?: unknown }
      | null;
    throw presignErrorFromStatus(presignResp.status, body);
  }
  const presigned: PresignResponse = await presignResp.json();
  const previewUrl = URL.createObjectURL(blob);

  if (presigned.already_uploaded || !presigned.upload) {
    return { asset: presigned.asset, previewUrl };
  }

  let putResp: Response;
  try {
    putResp = await fetch(presigned.upload.url, {
      method: presigned.upload.method,
      headers: presigned.upload.required_headers,
      body: blob,
    });
  } catch (e: any) {
    URL.revokeObjectURL(previewUrl);
    throw new AssetUploadError(
      { kind: 'network', cause: e?.message ?? 'fetch failed' },
      "Couldn't reach storage to upload the image. Check your connection and try again.",
    );
  }
  if (!putResp.ok) {
    URL.revokeObjectURL(previewUrl);
    throw new AssetUploadError(
      { kind: 'storage_rejected', status: putResp.status },
      "Storage rejected the upload. This is unexpected — please report it if it keeps happening.",
    );
  }

  return { asset: presigned.asset, previewUrl };
}

/// Fetch a short-lived presigned GET URL for a previously-uploaded asset.
/// Used to render image attachments loaded from message history (where we
/// don't have a `blob:` URL because it didn't originate this session).
export async function getAssetUrl(opts: {
  workspaceId: string;
  s3Key: string;
  apiUrl: string;
  token: string;
}): Promise<string> {
  const { workspaceId, s3Key, apiUrl, token } = opts;
  const resp = await fetch(
    `${apiUrl}/api/workspaces/${workspaceId}/assets/presign-download`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ s3_key: s3Key }),
    },
  );
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`presign-download failed (${resp.status}): ${text}`);
  }
  const data = await resp.json();
  return data.url as string;
}

/// Extract image File objects from a paste event. Used by the chat textarea's
/// onPaste handler so users can ⌘V a screenshot directly.
export function imageFilesFromClipboard(event: ClipboardEvent): File[] {
  const out: File[] = [];
  if (!event.clipboardData) return out;
  for (const item of event.clipboardData.items) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const f = item.getAsFile();
      if (f) out.push(f);
    }
  }
  return out;
}
